import * as React from "react";

/**
 * Custom theme provider — Phase 6a.2.
 *
 * Light/dark theme state persisted in `localStorage["ifrs16:theme"]` (matches
 * the project-wide `ifrs16:` namespace convention). Falls back to the user's
 * OS-level `prefers-color-scheme` on first visit. Toggling sets `class="dark"`
 * on `<html>` so Tailwind's `dark:` variants activate.
 *
 * We deliberately do NOT use `next-themes` (despite Nova preset including it)
 * — see prompt task 1 + Appendix J. A custom provider is small and matches
 * our localStorage key convention exactly.
 */

type Theme = "light" | "dark";

const STORAGE_KEY = "ifrs16:theme";

type ThemeContextValue = {
  theme: Theme;
  setTheme: (t: Theme) => void;
};

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyThemeToHtml(theme: Theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<Theme>(getInitialTheme);

  React.useEffect(() => {
    applyThemeToHtml(theme);
  }, [theme]);

  const setTheme = React.useCallback((t: Theme) => {
    setThemeState(t);
    try {
      window.localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // localStorage may be unavailable (private mode, quota); non-fatal.
    }
  }, []);

  const value = React.useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within <ThemeProvider>");
  }
  return ctx;
}
